import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import api from '../services/api';
import { useSocket } from '../contexts/SocketContext';

/* ── Gauge SVG arc ───────────────────────────────────────────── */
function Gauge({ value, label, unit = '%', color, size = 120 }) {
  const r = size / 2 - 10;
  const arc = d3.arc().innerRadius(r - 12).outerRadius(r).startAngle(-Math.PI * 0.75);
  const pct = Math.min(Math.max(value ?? 0, 0), 100) / 100;

  const bgPath = arc({ endAngle: Math.PI * 0.75 });
  const fgPath = arc({ endAngle: -Math.PI * 0.75 + pct * Math.PI * 1.5 });

  return (
    <div className="text-center" style={{ width: size }}>
      <svg width={size} height={size * 0.72} viewBox={`${-size / 2} ${-size / 2} ${size} ${size * 0.72}`}>
        <path d={bgPath} fill="var(--bs-border-color, #dee2e6)" />
        <path d={fgPath} fill={color} />
        <text textAnchor="middle" dy="0.1em" fontSize="1.3rem" fontWeight="700" fill="currentColor">
          {value != null ? `${Math.round(value)}${unit}` : '—'}
        </text>
      </svg>
      <div className="small text-muted mt-n1">{label}</div>
    </div>
  );
}

/* ── Sparkline + area chart (cpu or ram over time) ───────────── */
function TimeChart({ data, field, label, color, height = 110 }) {
  const ref = useRef();

  useEffect(() => {
    if (!data?.length || !ref.current) return;
    const el = ref.current;
    const w = el.clientWidth || 400;
    const h = height;
    const margin = { top: 6, right: 8, bottom: 20, left: 36 };

    d3.select(el).selectAll('*').remove();
    const svg = d3.select(el).append('svg').attr('width', w).attr('height', h);

    const x = d3.scaleTime()
      .domain(d3.extent(data, d => new Date(d.timestamp)))
      .range([margin.left, w - margin.right]);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d[field]) * 1.1 || 100])
      .nice()
      .range([h - margin.bottom, margin.top]);

    const area = d3.area()
      .x(d => x(new Date(d.timestamp)))
      .y0(y(0))
      .y1(d => y(d[field] || 0))
      .curve(d3.curveMonotoneX);

    const line = d3.line()
      .x(d => x(new Date(d.timestamp)))
      .y(d => y(d[field] || 0))
      .curve(d3.curveMonotoneX);

    svg.append('path').datum(data).attr('fill', color).attr('opacity', 0.15).attr('d', area);
    svg.append('path').datum(data).attr('fill', 'none').attr('stroke', color).attr('stroke-width', 1.5).attr('d', line);

    svg.append('g').attr('transform', `translate(0,${h - margin.bottom})`)
      .call(d3.axisBottom(x).ticks(6).tickFormat(d3.timeFormat('%H:%M')))
      .selectAll('text').attr('fill', 'currentColor').style('font-size', '0.65rem');

    svg.append('g').attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(4).tickFormat(v => `${v}%`))
      .selectAll('text').attr('fill', 'currentColor').style('font-size', '0.65rem');

    svg.selectAll('.domain,.tick line').attr('stroke', 'var(--bs-border-color, #dee2e6)');
  }, [data, field, color, height]);

  return (
    <div>
      <small className="fw-semibold">{label}</small>
      <div ref={ref} style={{ width: '100%', minHeight: height }} />
    </div>
  );
}

/* ── Format uptime ───────────────────────────────────────────── */
function formatUptime(seconds) {
  if (!seconds) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
}

function formatVolumeValue(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(1)} GB`;
}

function formatBandwidth(bytesPerSec) {
  if (bytesPerSec == null || Number.isNaN(Number(bytesPerSec))) return '—';
  const v = Number(bytesPerSec);
  if (v >= 1073741824) return `${(v / 1073741824).toFixed(1)} GB/s`;
  if (v >= 1048576) return `${(v / 1048576).toFixed(1)} MB/s`;
  if (v >= 1024) return `${(v / 1024).toFixed(1)} KB/s`;
  return `${Math.round(v)} B/s`;
}

function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return '—';
  const v = Number(bytes);
  if (v >= 1099511627776) return `${(v / 1099511627776).toFixed(1)} TB`;
  if (v >= 1073741824) return `${(v / 1073741824).toFixed(1)} GB`;
  if (v >= 1048576) return `${(v / 1048576).toFixed(1)} MB`;
  if (v >= 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${Math.round(v)} B`;
}

function LogicalVolumesPanel({ volumes }) {
  if (!Array.isArray(volumes) || volumes.length === 0) return null;

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h6 style={{ margin: 0, color: 'var(--slate-900)', fontWeight: 600 }}>Volúmenes lógicos</h6>
        <small style={{ color: 'var(--slate-400)' }}>Último snapshot</small>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {volumes.map((volume) => {
          const usage = Math.max(0, Math.min(100, Number(volume.used_percent) || 0));
          const barColor = usage >= 90 ? '#dc3545' : usage >= 75 ? '#f59e0b' : '#6366f1';
          return (
            <div key={`${volume.device || volume.name}-${volume.mountpoint || ''}`}
              style={{ background: 'var(--slate-100)', border: '1px solid var(--slate-200)', borderRadius: 8, padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--slate-900)' }}>{volume.name || volume.mountpoint || volume.device}</div>
                  <small style={{ color: 'var(--slate-400)' }}>{volume.device || 'sin device'} · {volume.filesystem || 'fs desconocido'}{volume.mountpoint ? ` · ${volume.mountpoint}` : ''}</small>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, color: barColor }}>{Math.round(usage)}%</div>
                  <small style={{ color: 'var(--slate-400)' }}>{formatVolumeValue(volume.used_gb)} / {formatVolumeValue(volume.total_gb)}</small>
                </div>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'var(--slate-200)', overflow: 'hidden' }}>
                <div style={{ width: `${usage}%`, height: '100%', borderRadius: 3, backgroundColor: barColor, transition: 'width 0.3s' }} />
              </div>
              <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--slate-400)' }}>Libre: {formatVolumeValue(volume.free_gb)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── NIC Traffic panel ────────────────────────────────────────── */
function NicTrafficPanel({ nics }) {
  if (!Array.isArray(nics) || nics.length === 0) return null;

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h6 style={{ margin: 0, color: 'var(--slate-900)', fontWeight: 600 }}>Tráfico de red</h6>
        <small style={{ color: 'var(--slate-400)' }}>Último snapshot</small>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {nics.map((nic) => {
          const hasErrors = (nic.errin || 0) + (nic.errout || 0) + (nic.dropin || 0) + (nic.dropout || 0) > 0;
          return (
            <div key={nic.interface}
              style={{ background: 'var(--slate-100)', border: '1px solid var(--slate-200)', borderRadius: 8, padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                <div style={{ fontWeight: 600, color: 'var(--slate-900)' }}>{nic.interface}</div>
                {hasErrors && (
                  <span style={{ fontSize: '0.75rem', color: '#dc3545', fontWeight: 500 }}>
                    ⚠ Err: {(nic.errin || 0) + (nic.errout || 0)} · Drop: {(nic.dropin || 0) + (nic.dropout || 0)}
                  </span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '1rem', color: '#198754' }}>↓</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#198754', fontSize: '0.95rem' }}>{formatBandwidth(nic.bytes_recv_sec)}</div>
                    <small style={{ color: 'var(--slate-400)' }}>Total: {formatBytes(nic.bytes_recv)} · {(nic.packets_recv ?? 0).toLocaleString()} pkt</small>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '1rem', color: '#0d6efd' }}>↑</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0d6efd', fontSize: '0.95rem' }}>{formatBandwidth(nic.bytes_sent_sec)}</div>
                    <small style={{ color: 'var(--slate-400)' }}>Total: {formatBytes(nic.bytes_sent)} · {(nic.packets_sent ?? 0).toLocaleString()} pkt</small>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Windows Services panel ──────────────────────────────────── */
function ServicesPanel({ services }) {
  const [filter, setFilter] = useState('');
  if (!Array.isArray(services) || services.length === 0) return null;

  const filtered = filter
    ? services.filter(s =>
        (s.display_name || s.name || '').toLowerCase().includes(filter.toLowerCase())
      )
    : services;

  const running = services.filter(s => s.status === 'running').length;
  const stopped = services.filter(s => s.status === 'stopped').length;

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h6 style={{ margin: 0, color: 'var(--slate-900)', fontWeight: 600, display: 'inline' }}>Servicios</h6>
          <small style={{ color: 'var(--slate-400)', marginLeft: 8 }}>
            <span style={{ color: '#198754' }}>{running} activos</span>
            {stopped > 0 && <span style={{ color: '#dc3545', marginLeft: 6 }}>{stopped} parados</span>}
            {' '}· {services.length} total
          </small>
        </div>
        <input
          type="text"
          placeholder="Filtrar servicios…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            fontSize: '0.8rem', padding: '4px 10px', borderRadius: 6,
            border: '1px solid var(--slate-200)', background: 'var(--slate-50)',
            color: 'var(--slate-900)', minWidth: 180
          }}
        />
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--slate-200)', borderRadius: 8 }}>
        <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--slate-100)', position: 'sticky', top: 0 }}>
              <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--slate-900)' }}>Servicio</th>
              <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--slate-900)' }}>Nombre</th>
              <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: 'var(--slate-900)' }}>Estado</th>
              <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: 'var(--slate-900)' }}>Inicio</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(svc => (
              <tr key={svc.name} style={{ borderTop: '1px solid var(--slate-200)' }}>
                <td style={{ padding: '5px 10px', color: 'var(--slate-700)' }}>{svc.display_name || svc.name}</td>
                <td style={{ padding: '5px 10px', color: 'var(--slate-400)', fontFamily: 'monospace', fontSize: '0.78rem' }}>{svc.name}</td>
                <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600,
                    background: svc.status === 'running' ? '#d1fae5' : '#fee2e2',
                    color: svc.status === 'running' ? '#065f46' : '#991b1b'
                  }}>
                    {svc.status === 'running' ? 'Activo' : 'Parado'}
                  </span>
                </td>
                <td style={{ padding: '5px 10px', textAlign: 'center', color: 'var(--slate-400)', fontSize: '0.78rem' }}>
                  {svc.start_type === 'automatic' || svc.start_type === 'auto' ? 'Auto' : svc.start_type || '—'}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 12, textAlign: 'center', color: 'var(--slate-400)' }}>Sin resultados</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Main panel ──────────────────────────────────────────────── */
export default function VirtVMMonitoringPanel({ serverId, companyId, hasAgent = false }) {
  const socket = useSocket();
  const [metrics, setMetrics] = useState([]);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const fetchMetrics = useCallback(async ({ silent = false } = {}) => {
    if (!serverId || !companyId) return;
    if (!silent) setLoading(true);
    try {
      const response = await api.getVirtVMMetrics(serverId, companyId, hours);
      setMetrics(Array.isArray(response?.data) ? response.data : []);
      setLoadError(null);
    } catch (err) {
      // Antes se tragaba el error y el panel decía «instale el agente» aunque
      // el agente estuviera instalado y el fallo fuera de la API.
      if (!silent) setLoadError(err?.message || 'No se pudieron leer las métricas');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [serverId, companyId, hours]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (!serverId || !companyId) return undefined;
    const timerId = window.setInterval(() => {
      fetchMetrics({ silent: true });
    }, 30000);
    return () => window.clearInterval(timerId);
  }, [serverId, companyId, fetchMetrics]);

  // Real-time append via WebSocket
  useEffect(() => {
    if (!socket) return;

    const onConnect = () => {
      fetchMetrics({ silent: true });
    };

    const onData = (payload) => {
      const payloadServerId = payload?.virt_vm_id ?? payload?.id;
      if (Number(payloadServerId) !== Number(serverId)) return;
      if (payload?.ci_type !== 'virt_vm') return;
      
      setMetrics((prev) => {
        const nextPoint = { ...payload, server_id: payloadServerId };
        const existingIndex = prev.findIndex((item) =>
          (nextPoint.id && item.id === nextPoint.id)
          || (nextPoint.timestamp && item.timestamp === nextPoint.timestamp)
        );
        if (existingIndex !== -1) {
          const next = [...prev];
          next[existingIndex] = { ...next[existingIndex], ...nextPoint };
          return next;
        }
        return [...prev, nextPoint]
          .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0))
          .slice(-500);
      });
    };
    socket.on('connect', onConnect);
    socket.on('monitoring:data', onData);
    return () => {
      socket.off('connect', onConnect);
      socket.off('monitoring:data', onData);
    };
  }, [socket, serverId, fetchMetrics]);

  const latest = metrics[metrics.length - 1];
  const cpuColor = (latest?.cpu_percent ?? 0) > 90 ? '#dc3545' : (latest?.cpu_percent ?? 0) > 70 ? '#ffc107' : '#198754';
  const ramColor = (latest?.ram_percent ?? 0) > 90 ? '#dc3545' : (latest?.ram_percent ?? 0) > 70 ? '#ffc107' : '#198754';

  if (loading) return <div className="text-center p-3"><div className="spinner-border spinner-border-sm" /></div>;

  if (!metrics.length) {
    const message = loadError
      ? `No se han podido leer las métricas: ${loadError}`
      : hasAgent
        ? 'El agente está instalado pero todavía no ha enviado métricas. Se envía una muestra cada minuto; si persiste, revisa el estado del agente en la ficha.'
        : 'Sin datos de monitorización. Instale el agente CMDB para recoger métricas.';
    return <div className="text-muted text-center p-4">{message}</div>;
  }

  return (
    <div>
      {/* Time range selector */}
      <div className="d-flex gap-2 mb-3">
        {[1, 6, 12, 24, 72, 168].map(h => (
          <button key={h}
            className={`btn btn-sm ${hours === h ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setHours(h)}
          >{h < 24 ? `${h}h` : `${h / 24}d`}</button>
        ))}
      </div>

      {/* Gauges row */}
      <div className="d-flex gap-4 justify-content-center mb-3">
        <Gauge value={latest?.cpu_percent} label="CPU" color={cpuColor} />
        <Gauge value={latest?.ram_percent} label="RAM" color={ramColor} />
        <div className="text-center d-flex flex-column align-items-center justify-content-center" style={{ minWidth: 120 }}>
          <div className="fs-5 fw-bold">{latest?.ram_used_gb ?? '—'} / {latest?.ram_total_gb ?? '—'} GB</div>
          <small className="text-muted">RAM Usada</small>
          <div className="fs-6 mt-1">{formatUptime(latest?.uptime_seconds)}</div>
          <small className="text-muted">Uptime</small>
        </div>
      </div>

      {/* Time-series charts */}
      <div className="row g-3">
        <div className="col-md-6">
          <TimeChart data={metrics} field="cpu_percent" label="CPU %" color="#0d6efd" />
        </div>
        <div className="col-md-6">
          <TimeChart data={metrics} field="ram_percent" label="RAM %" color="#6f42c1" />
        </div>
      </div>

      <LogicalVolumesPanel volumes={latest?.logical_volumes} />

      <NicTrafficPanel nics={latest?.nic_traffic} />

      <ServicesPanel services={latest?.services} />
    </div>
  );
}
